import { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { clientAPI } from '../API/clientAPI';
import { storeAPI } from '../API/storeAPI';
import { deviceAPI } from '../API/deviceAPI';
import AuthContext from './CustomContext';

const initialState = {
  clients: [],
  stores: [],
  devices: [],
  selectedClient: null,
  selectedStore: null,
  isLoading: false,
  error: null,
};

const handlers = {
  SET_LOADING: (state, { payload }) => ({ ...state, isLoading: payload }),
  SET_CLIENTS: (state, { payload }) => ({ ...state, clients: payload, isLoading: false }),
  SET_STORES: (state, { payload }) => ({ ...state, stores: payload, isLoading: false }),
  SET_DEVICES: (state, { payload }) => ({ ...state, devices: payload, isLoading: false }),
  SELECT_CLIENT: (state, { payload }) => ({ ...state, selectedClient: payload, selectedStore: null, stores: [], devices: [] }),
  SELECT_STORE: (state, { payload }) => ({ ...state, selectedStore: payload, devices: [] }),
  SET_ERROR: (state, { payload }) => ({ ...state, error: payload, isLoading: false }),
  RESET: () => ({ ...initialState }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const GatewayContagemContext = createContext({ ...initialState });

export const GatewayContagemProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isAuthenticated } = useContext(AuthContext);

  const clientsLoadingRef = useRef(false);
  const clientsLoadedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch({ type: 'RESET' });
      clientsLoadedRef.current = false;
      clientsLoadingRef.current = false;
    }
  }, [isAuthenticated]);

  const loadClients = useCallback(async () => {
    if (clientsLoadingRef.current || clientsLoadedRef.current) {
      return;
    }

    clientsLoadingRef.current = true;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const data = await clientAPI.list({ page: 1, itemsPerPage: 100 });

      if (data && !data.error) {
        dispatch({
          type: 'SET_CLIENTS',
          payload: data.value?.items || [],
        });

        clientsLoadedRef.current = true;
      } else {
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to load clients',
        });
      }
    } finally {
      clientsLoadingRef.current = false;
    }
  }, []);

  const loadStores = useCallback(async (clientId) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const data = await storeAPI.getByClient(clientId);
    if (data && !data.error) {
      const storeList = data.value || [];
      dispatch({ type: 'SET_STORES', payload: storeList });
      if (storeList.length === 1) {
        dispatch({ type: 'SELECT_STORE', payload: storeList[0] });
      }
    } else {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load stores' });
    }
  }, []);

  const loadDevices = useCallback(async (storeId) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const data = await deviceAPI.getByStore(storeId);
    if (data && !data.error) {
      dispatch({ type: 'SET_DEVICES', payload: data.value || [] });
    } else {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load devices' });
    }
  }, []);

  const selectClient = useCallback((client) => {
    dispatch({ type: 'SELECT_CLIENT', payload: client });
    if (client) loadStores(client.id);
  }, [loadStores]);

  const selectStore = useCallback((store) => {
    dispatch({ type: 'SELECT_STORE', payload: store });
    if (store) loadDevices(store.id);
  }, [loadDevices]);

  return (
    <GatewayContagemContext.Provider value={{ ...state, loadClients, loadStores, loadDevices, selectClient, selectStore }}>
      {children}
    </GatewayContagemContext.Provider>
  );
};

GatewayContagemProvider.propTypes = { children: PropTypes.node.isRequired };

export default GatewayContagemContext;
